async function setupMonaco() {
  if (typeof window !== "undefined") {
    const monacoScript = Array.from(document.head.querySelectorAll("script")).find(
      (script) => script.src && script.src.includes("monaco") && script.src.includes("vs/loader")
    ) || await appendMonacoEditorScript();
    window.require.config({
      paths: {
        vs: monacoScript.src.replace(/\/vs\/.*$/u, "/vs")
      }
    });
  }
}
async function appendMonacoEditorScript() {
  let error = new Error();
  const urlList = [
    "https://cdn.jsdelivr.net/npm/monaco-editor/dev/vs/loader.min.js",
    "https://unpkg.com/monaco-editor@latest/min/vs/loader.js"
  ];
  if (typeof MONACO_EDITOR_VERSION !== "undefined") {
    urlList.unshift(
      `https://cdnjs.cloudflare.com/ajax/libs/monaco-editor/${MONACO_EDITOR_VERSION}/min/vs/loader.min.js`,
      `https://cdn.jsdelivr.net/npm/monaco-editor@${MONACO_EDITOR_VERSION}/dev/vs/loader.min.js`,
      `https://unpkg.com/monaco-editor/${MONACO_EDITOR_VERSION}/min/vs/loader.min.js`
    );
  }
  for (const url of urlList) {
    try {
      return await appendScript(url);
    } catch (e) {
      console.warn(`Failed to retrieve resource from ${url}`);
      error = e;
    }
  }
  throw error;
}
async function appendScript(src) {
  const script = document.createElement("script");
  return new Promise((resolve, reject) => {
    script.src = src;
    script.onload = () => {
      script.onload = null;
      watch();
      function watch() {
        if (window.require) {
          resolve(script);
          return;
        }
        setTimeout(watch, 200);
      }
    };
    script.onerror = (e) => {
      reject(e);
      document.head.removeChild(script);
    };
    document.head.append(script);
  });
}
let setupedMonaco = null;
let editorLoaded = null;
function loadMonacoEngine() {
  return setupedMonaco || (setupedMonaco = setupMonaco());
}
function loadMonacoEditor() {
  if (editorLoaded) {
    return editorLoaded;
  }
  return editorLoaded = (async () => {
    const monaco = await loadModuleFromMonaco("vs/editor/editor.main");
    setupEnhancedLanguages(monaco);
    return monaco;
  })();
}
async function loadModuleFromMonaco(moduleName) {
  await loadMonacoEngine();
  return new Promise((resolve) => {
    if (typeof window !== "undefined") {
      window.require([moduleName], (r) => {
        resolve(r);
      });
    }
  });
}
async function setupEnhancedLanguages(monaco) {
  const dynamicImport = new Function(
    "file",
    "return import(file)"
  );
  dynamicImport(
    "https://cdn.skypack.dev/@ota-meshi/site-kit-monarch-syntaxes/astro"
  ).then((module) => module.setupAstroLanguage(monaco));
  dynamicImport(
    "https://cdn.skypack.dev/@ota-meshi/site-kit-monarch-syntaxes/stylus"
  ).then((module) => module.setupStylusLanguage(monaco));
  dynamicImport(
    "https://cdn.skypack.dev/@ota-meshi/site-kit-monarch-syntaxes/svelte"
  ).then((module) => module.setupSvelteLanguage(monaco));
  dynamicImport(
    "https://cdn.skypack.dev/@ota-meshi/site-kit-monarch-syntaxes/toml"
  ).then((module) => module.setupTomlLanguage(monaco));
}

async function setupMonacoEditor({
  init,
  listeners,
  useDiffEditor,
  rootElement
}) {
  const monaco = await loadMonacoEditor();
  const language = init.language;
  const options = {
    value: init.value,
    readOnly: init.readOnly,
    theme: "vs-dark",
    language,
    automaticLayout: true,
    fontSize: 14,
    fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    minimap: {
      enabled: false
    },
    renderControlCharacters: true,
    guides: {
      indentation: true
    },
    renderValidationDecorations: "on",
    renderWhitespace: "boundary",
    scrollBeyondLastLine: false
  };
  if (useDiffEditor) {
    const diffEditor = monaco.editor.createDiffEditor(rootElement, {
      originalEditable: true,
      useInlineViewWhenSpaceIsLimited: false,
      renderSideBySideInlineBreakpoint: 600,
      ...options
    });
    const original = monaco.editor.createModel(init.value, language);
    const modified = monaco.editor.createModel(
      init.right?.value || "",
      language
    );
    const leftEditor = diffEditor.getOriginalEditor();
    const rightEditor = diffEditor.getModifiedEditor();
    rightEditor.updateOptions({ readOnly: true });
    diffEditor.setModel({ original, modified });
    original.onDidChangeContent(() => {
      const value = original.getValue();
      listeners?.onChangeValue?.(value);
    });
    leftEditor.onDidChangeCursorPosition((evt) => {
      listeners?.onDidChangeCursorPosition?.(evt);
    });
    leftEditor.onDidFocusEditorText(() => {
      listeners?.onFocus?.();
    });
    const registerCodeActionProvider2 = buildRegisterCodeActionProvider(
      leftEditor,
      language
    );
    const result2 = {
      setModelLanguage: (lang) => {
        for (const model of [original, modified]) {
          monaco.editor.setModelLanguage(model, lang);
        }
        registerCodeActionProvider2.setLanguage(lang);
      },
      setLeftValue: (code) => {
        const value = original.getValue();
        if (code !== value) {
          original.setValue(code);
        }
      },
      setRightValue: (code) => {
        const value = modified.getValue();
        if (code !== value) {
          modified.setValue(code);
        }
      },
      setLeftMarkers: (markers) => {
        void updateMarkers(leftEditor, markers);
      },
      setRightMarkers: (markers) => {
        void updateMarkers(rightEditor, markers);
      },
      getLeftEditor: () => leftEditor,
      getRightEditor: () => rightEditor,
      registerCodeActionProvider: registerCodeActionProvider2.register,
      disposeEditor: () => {
        registerCodeActionProvider2.dispose();
        leftEditor.getModel()?.dispose();
        rightEditor.getModel()?.dispose();
        leftEditor.dispose();
        rightEditor.dispose();
        diffEditor.dispose();
      }
    };
    if (init.markers)
      result2.setLeftMarkers(init.markers);
    if (init.right?.markers)
      result2.setRightMarkers(init.right?.markers);
    return result2;
  }
  const standaloneEditor = monaco.editor.create(rootElement, options);
  standaloneEditor.onDidChangeModelContent(() => {
    const value = standaloneEditor.getValue();
    listeners?.onChangeValue?.(value);
  });
  standaloneEditor.onDidChangeCursorPosition((evt) => {
    listeners?.onDidChangeCursorPosition?.(evt);
  });
  standaloneEditor.onDidFocusEditorText(() => {
    listeners?.onFocus?.();
  });
  const registerCodeActionProvider = buildRegisterCodeActionProvider(
    standaloneEditor,
    language
  );
  const result = {
    setModelLanguage: (lang) => {
      const model = standaloneEditor.getModel();
      if (model) {
        monaco.editor.setModelLanguage(model, lang);
      }
      registerCodeActionProvider.setLanguage(lang);
    },
    setLeftValue: (code) => {
      const value = standaloneEditor.getValue();
      if (code !== value) {
        standaloneEditor.setValue(code);
      }
    },
    setRightValue: () => {
    },
    setLeftMarkers: (markers) => {
      void updateMarkers(standaloneEditor, markers);
    },
    setRightMarkers: () => {
    },
    getLeftEditor: () => standaloneEditor,
    getRightEditor: () => null,
    registerCodeActionProvider: registerCodeActionProvider.register,
    disposeEditor: () => {
      registerCodeActionProvider.dispose();
      standaloneEditor.getModel()?.dispose();
      standaloneEditor.dispose();
    }
  };
  if (init.markers)
    result.setLeftMarkers(init.markers);
  return result;
  function updateMarkers(editor, markers) {
    const model = editor.getModel();
    const id = editor.getId();
    monaco.editor.setModelMarkers(
      model,
      id,
      JSON.parse(JSON.stringify(markers))
    );
  }
  function buildRegisterCodeActionProvider(editor, initLanguage) {
    let codeActionProviderDisposable = {
      dispose: () => {
      }
    };
    let currProvideCodeActions = null;
    let currLanguage = initLanguage;
    function register() {
      codeActionProviderDisposable.dispose();
      codeActionProviderDisposable = monaco.languages.registerCodeActionProvider(currLanguage, {
        provideCodeActions(model, ...args) {
          if (!currProvideCodeActions || editor.getModel().uri !== model.uri) {
            return {
              actions: [],
              dispose() {
              }
            };
          }
          return currProvideCodeActions(model, ...args);
        }
      });
    }
    return {
      setLanguage: (lang) => {
        currLanguage = lang;
        register();
      },
      register: (provideCodeActions) => {
        currProvideCodeActions = provideCodeActions;
        register();
      },
      dispose() {
        codeActionProviderDisposable.dispose();
      }
    };
  }
}

export { loadMonacoEditor, setupMonacoEditor };
